/**
 * Редактор текстовой альтернативы для каналов без кнопок
 * Дата: 07.02.2026 - Создан для TODO-053 (Template Buttons ↔ Auto-Replies)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/pages/Templates.tsx - используется в форме создания/редактирования шаблонов
 * - frontend/src/api/templates.ts - Template интерфейс с text_alternative
 * 
 * КАК РАБОТАЕТ:
 * 1. Позволяет ввести текстовую альтернативу для каналов без поддержки кнопок (TG Client, MAX, SMS)
 * 2. Автогенерация из кнопок: «Ответьте 1 — Отлично, 2 — Плохо»
 * 3. Превью: как будет выглядеть в TG Client / SMS
 * 
 * СХЕМА:
 * Templates.tsx → TextAlternativeEditor → formData.text_alternative
 * TextAlternativeEditor → generateFromButtons() → автогенерация из buttons
 */
import { useState } from 'react';
import { Button, Textarea } from '@/components/ui';
import { TemplateButton } from '@/api/templates';
import { RefreshCw } from 'lucide-react';

interface TextAlternativeEditorProps {
  textAlternative: string;
  buttons: TemplateButton[];
  onChange: (text: string) => void;
  testId?: string;
}

export function TextAlternativeEditor({ 
  textAlternative, 
  buttons, 
  onChange, 
  testId 
}: TextAlternativeEditorProps) {
  const [showPreview, setShowPreview] = useState(false);

  const generateFromButtons = () => {
    if (buttons.length === 0) {
      onChange('');
      return;
    }
    
    const lines = ['Ответьте цифрой:'];
    buttons.forEach((btn, index) => {
      lines.push(`${index + 1} — ${btn.text}`);
    });
    onChange(lines.join('\n'));
  };

  return (
    <div className="space-y-3" data-testid={testId || 'text-alternative-editor'}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-neutral-700">
          📝 Текстовая альтернатива для каналов без кнопок
        </label>
        {buttons.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={generateFromButtons}
            testId="btn-generate-alternative"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Сгенерировать из кнопок
          </Button>
        )}
      </div>
      
      <Textarea
        value={textAlternative}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ответьте цифрой:&#10;1 — Отлично&#10;2 — Плохо"
        rows={4}
        testId="textarea-text-alternative"
      />
      
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-sm text-primary-600 hover:text-primary-700"
          data-testid="btn-toggle-preview"
        >
          {showPreview ? 'Скрыть' : 'Показать'} превью
        </button>
      </div>
      
      {showPreview && (
        <div className="border rounded-lg p-4 bg-neutral-50">
          <div className="text-sm font-medium text-neutral-700 mb-2">
            Превью (TG Client / MAX / SMS):
          </div>
          <div className="bg-white border rounded p-3 whitespace-pre-wrap text-sm">
            {textAlternative || '(не задано)'}
          </div>
          {textAlternative && textAlternative.length > 160 && (
            <p className="text-xs text-orange-600 mt-2">
              ⚠️ Текст длиннее 160 символов. Для SMS будет укорочен.
            </p>
          )}
        </div>
      )}
      
      <p className="text-xs text-neutral-500">
        💡 Эта альтернатива будет добавлена к тексту шаблона при отправке через каналы без поддержки кнопок 
        (Telegram Client, MAX, SMS).
      </p>
    </div>
  );
}
